import React, { useMemo, useState } from "react";
import franceMapData from "@svg-maps/france.departments";
import {
  ZONE_COLORS,
  TEMPERATURE_TABLE,
  DEPARTEMENT_TO_ZONE,
  DEPARTEMENT_NAMES,
} from "../lib/climate";

/**
 * Real geographic France map by departments (96 paths), colored by climatic zone.
 * Clicking on any department selects its zone and notifies the parent.
 * Data source: @svg-maps/france.departments (CC-BY 4.0, Victor Cazanave).
 */
const VIEW_BOX = franceMapData.viewBox; // "0 0 613 585"
const LOCATIONS = franceMapData.locations; // [{id, name, path}]

// Approximate centroids per zone for label placement (visual cluster centers).
const ZONE_LABEL_POS = {
  A: { x: 478, y: 470 },   // Méditerranée
  B: { x: 105, y: 270 },   // Façade Atlantique
  C: { x: 195, y: 470 },   // Sud-Ouest
  D: { x: 270, y: 200 },   // Bassin Parisien
  E: { x: 295, y: 360 },   // Massif Central
  F: { x: 305, y: 80 },    // Hauts-de-France
  G: { x: 420, y: 290 },   // Est / Bourgogne / Rhône-Alpes
  H: { x: 480, y: 170 },   // Lorraine / Franche-Comté
  I: { x: 535, y: 110 },   // Alsace
};

export default function FranceMap({ selectedZone, onSelectZone, onSelectDept, selectedDept, altitude = 200 }) {
  const [hoveredDept, setHoveredDept] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(null);

  // Group departments by zone for legend interactions.
  const byZone = useMemo(() => {
    const map = {};
    for (const loc of LOCATIONS) {
      const z = DEPARTEMENT_TO_ZONE[loc.id];
      if (!z) continue;
      (map[z] ||= []).push(loc.id);
    }
    return map;
  }, []);

  const handleDeptClick = (dept) => {
    const zone = DEPARTEMENT_TO_ZONE[dept];
    onSelectDept?.(dept, zone);
    if (zone) onSelectZone?.(zone);
  };

  return (
    <div className="relative w-full">
      <div className="relative w-full overflow-hidden rounded-xl bg-slate-50/50 border border-slate-100">
        <svg
          viewBox={VIEW_BOX}
          className="w-full h-auto max-h-[560px] select-none block"
          xmlns="http://www.w3.org/2000/svg"
          data-testid="france-map"
          aria-label="Carte interactive des départements français par zone climatique"
        >
          <defs>
            <filter id="deptShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="1" stdDeviation="1.2" floodOpacity="0.18" />
            </filter>
          </defs>

          {LOCATIONS.map((loc) => {
            const zone = DEPARTEMENT_TO_ZONE[loc.id];
            const fill = zone ? ZONE_COLORS[zone] : "#cbd5e1";
            const isSelected = selectedDept === loc.id;
            const inSelectedZone = selectedZone && zone === selectedZone;
            const isHovered = hoveredDept === loc.id;
            const zoneHovered = hoveredZone && zone === hoveredZone;

            let opacity = 0.78;
            if (isSelected) opacity = 1;
            else if (inSelectedZone || zoneHovered) opacity = 0.95;
            else if (isHovered) opacity = 0.92;

            return (
              <path
                key={loc.id}
                d={loc.path}
                fill={fill}
                fillOpacity={opacity}
                stroke={isSelected ? "#0f172a" : "#ffffff"}
                strokeWidth={isSelected ? 1.6 : 0.55}
                style={{ transition: "fill-opacity 180ms ease-out, stroke-width 180ms ease-out" }}
                onClick={() => handleDeptClick(loc.id)}
                onMouseEnter={() => setHoveredDept(loc.id)}
                onMouseLeave={() => setHoveredDept(null)}
                className="cursor-pointer"
                data-testid={`dept-path-${loc.id}`}
              >
                <title>
                  {loc.name} ({loc.id}) — Zone {zone || "?"}
                </title>
              </path>
            );
          })}

          {/* Zone letter labels — drawn above all department paths */}
          {Object.entries(ZONE_LABEL_POS).map(([zone, pos]) => (
            <g key={zone} className="pointer-events-none">
              <circle
                cx={pos.x}
                cy={pos.y}
                r={14}
                fill="#ffffff"
                fillOpacity={0.92}
                stroke={ZONE_COLORS[zone]}
                strokeWidth={2}
              />
              <text
                x={pos.x}
                y={pos.y + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                className="font-display"
                fontSize={13}
                fontWeight="800"
                fill={ZONE_COLORS[zone]}
              >
                {zone}
              </text>
            </g>
          ))}

          {/* Hovered department tooltip (text in top-left) */}
          {hoveredDept && DEPARTEMENT_TO_ZONE[hoveredDept] && (
            <g className="pointer-events-none">
              <rect
                x={10}
                y={10}
                rx={8}
                ry={8}
                width={210}
                height={48}
                fill="#0f172a"
                fillOpacity={0.92}
              />
              <text x={22} y={32} fill="#ffffff" fontSize={12} fontWeight="600">
                {DEPARTEMENT_NAMES[hoveredDept]} ({hoveredDept})
              </text>
              <text x={22} y={48} fill="#cbd5e1" fontSize={11} className="font-mono-data">
                Zone {DEPARTEMENT_TO_ZONE[hoveredDept]} ·{" "}
                {TEMPERATURE_TABLE[DEPARTEMENT_TO_ZONE[hoveredDept]]?.[
                  Math.min(altitude, 1200)
                ] ?? TEMPERATURE_TABLE[DEPARTEMENT_TO_ZONE[hoveredDept]]?.[200]}
                °C base
              </text>
            </g>
          )}

          {/* North arrow */}
          <g transform="translate(580, 20)">
            <circle cx="0" cy="0" r="12" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" />
            <text
              x="0"
              y="3"
              textAnchor="middle"
              fontSize="10"
              fontWeight="700"
              fill="#0f172a"
              className="font-display"
            >
              N
            </text>
          </g>
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="map-legend">
        {Object.keys(ZONE_COLORS).map((z) => (
          <button
            key={z}
            type="button"
            onClick={() => onSelectZone?.(z)}
            onMouseEnter={() => setHoveredZone(z)}
            onMouseLeave={() => setHoveredZone(null)}
            data-testid={`legend-zone-${z}`}
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
              selectedZone === z
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
            }`}
          >
            <span
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: ZONE_COLORS[z] }}
            />
            <span>Zone {z}</span>
            <span className="font-mono-data text-[10px] text-slate-400">
              {byZone[z]?.length || 0}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
